import { MapPin, Calendar, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { 
  RACE_TYPE_LABELS, 
  getRaceTypeBadgeClass, 
  getRaceTypeBorderClass,
  formatDatePT 
} from '../lib/utils';

export const RaceCard = ({ race, index = 0, onClick }) => {
  const date = new Date(race.date);
  const day = date.getDate();
  const weekday = date.toLocaleDateString('pt-PT', { weekday: 'short' }).toUpperCase();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`race-card bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 border-l-4 ${getRaceTypeBorderClass(race.race_type)} rounded-lg overflow-hidden cursor-pointer group`}
      onClick={onClick}
      data-testid={`race-card-${race.id}`}
    >
      <div className="flex items-stretch">
        {/* Date section */}
        <div className="flex flex-col items-center justify-center px-4 py-4 bg-slate-50 dark:bg-slate-800/50 min-w-[80px]">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {weekday}
          </span>
          <span className="text-3xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {day}
          </span>
        </div>
        
        {/* Info section */}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 
                className="font-bold text-lg text-slate-900 dark:text-white truncate group-hover:text-red-600 transition-colors"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {race.name}
              </h3>
              <div className="flex items-center gap-1 mt-1 text-slate-500 dark:text-slate-400 text-sm">
                <MapPin size={14} />
                <span className="truncate">{race.location}, {race.district}</span>
              </div>
            </div>
            
            <Badge className={`${getRaceTypeBadgeClass(race.race_type)} shrink-0`}>
              {RACE_TYPE_LABELS[race.race_type]}
            </Badge>
          </div>
          
          {/* Distances and meta */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex flex-wrap gap-1">
              {race.distances.slice(0, 3).map((dist) => (
                <span 
                  key={dist}
                  className="text-xs font-medium px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-300"
                >
                  {dist}
                </span>
              ))}
              {race.distances.length > 3 && (
                <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-300">
                  +{race.distances.length - 3}
                </span>
              )}
            </div>
            
            {race.price && (
              <span className="text-sm font-bold text-red-600">
                {race.price}
              </span>
            )}
          </div>
          
          {/* Website link */}
          {race.website && (
            <a 
              href={race.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
              data-testid={`race-website-${race.id}`}
            >
              <ExternalLink size={12} />
              Ver prova
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RaceCard;
